import { Component, EventEmitter, Input, Output, computed, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ThemeService } from '../services/theme';
import { ResponsiveService } from '../services/responsive';
import { v3LayoutFlags } from '../services/v3-layout';
import { V3PoweredByHeader } from '../v3-powered-by-header/v3-powered-by-header';
import { V3StockHeader } from '../v3-stock-header/v3-stock-header';
import { V3AnalysisHeader } from '../v3-analysis-header/v3-analysis-header';
import { V3FinnetBreadcrumb } from '../v3-finnet-breadcrumb/v3-finnet-breadcrumb';
import { V3TabNavigation } from '../v3-tab-navigation/v3-tab-navigation';
import { V3ZeroDesktopHeader } from '../v3-zero-desktop-header/v3-zero-desktop-header';
import { V3ConditionsFooter } from '../v3-conditions-footer/v3-conditions-footer';
import { V3FinnetSubpageCard } from '../v3-finnet-subpage-card/v3-finnet-subpage-card';
import { V3ZeroSubpageCard } from '../v3-zero-subpage-card/v3-zero-subpage-card';
import { V3RegwallInfo } from '../v3-regwall-info/v3-regwall-info';
import { V3ChatCard } from '../v3-chat-widget/v3-chat-card';

// Orchestrates V3's header/nav chrome — see CLAUDE.md in this directory for the full
// gating table. Tab content itself is projected in via <ng-content> from core.html,
// not owned by this component — see v3-root.html for where it lands. On desktop
// subpages, that content is wrapped in V3FinnetSubpageCard (Finnet) or V3ZeroSubpageCard
// (Zero) — see below — content components themselves stay unaware of this, so every
// future ported section gets the same treatment automatically.
@Component({
  selector: 'app-v3-root',
  templateUrl: './v3-root.html',
  styleUrl: './v3-root.scss',
  standalone: true,
  imports: [
    V3PoweredByHeader,
    V3StockHeader,
    V3AnalysisHeader,
    V3FinnetBreadcrumb,
    V3TabNavigation,
    V3ZeroDesktopHeader,
    V3ConditionsFooter,
    V3FinnetSubpageCard,
    V3ZeroSubpageCard,
    V3RegwallInfo,
    V3ChatCard,
    NgTemplateOutlet,
  ],
})
export class V3Root {
  @Input() stockName = '';
  @Input() stockLogo!: string | null;
  @Input() price!: number | null;
  @Input() prevPrice!: number | null;
  @Input() isMainPage = true;
  // A tab's info page: its subpage's chrome minus the desktop card wrapper (AI-637).
  @Input() isInfoPage = false;
  // The per-tab info icon on the four subpage hosts — Core.showInfoButton.
  @Input() showInfoButton = true;
  @Input() pageTitle = '';
  @Input() pageIcon: string | null = null;
  // The active tab's overview-card timestamp — mobile analysis header and desktop subpage cards.
  @Input() updatedAt: Date | null = null;
  @Input() activeTab = 'main';
  @Input() tabs: { label: string; id: string }[] = [];
  @Input() companyAvailable = true;
  // Finnet guest mode — renders V3RegwallInfo over the blurred page.
  @Input() showRegwall = false;
  @Output() companyClicked = new EventEmitter<void>();
  @Output() subpageBackClicked = new EventEmitter<void>();
  // Zero's close buttons (desktop powered-by row, mobile overview header) — Core.parentGoBack().
  @Output() closeClicked = new EventEmitter<void>();
  @Output() tabChange = new EventEmitter<string>();
  @Output() infoClicked = new EventEmitter<void>();

  private readonly themeService = inject(ThemeService);
  private readonly responsive = inject(ResponsiveService);
  private readonly layout = v3LayoutFlags(this.themeService, this.responsive);

  readonly showZeroMobileHeader = computed(
    () => !this.layout.isFinnet() && this.responsive.isMobile(),
  );

  // Zero web desktop — one persistent header for both main and subpages; switching
  // sections happens via the embedded tab nav, not a back/forward navigation concept.
  readonly showZeroDesktopHeader = this.layout.isZeroDesktop;

  // Finnet's subpage-nav equivalent — every Finnet layout except finnetWeb desktop: all three
  // variants on mobile, plus finnetApp at any width (Flutter-embedded, no desktop chrome of its
  // own, so a tablet or landscape phone still needs this way back — AI-640).
  // A plain method, not computed(): isMainPage is a plain @Input(), not a signal, so
  // computed() would never see it change and this would get stuck at its first value.
  showFinnetBreadcrumb(): boolean {
    return this.layout.isFinnet() && !this.layout.isFinnetDesktop() && !this.isMainPage;
  }

  // finnetWeb desktop only — finnetApp never gets desktop chrome, it keeps the breadcrumb above.
  readonly showFinnetTabNavigation = this.layout.isFinnetDesktop;

  // finnetWeb desktop subpages only, not the main/overview page or a tab's info page — same
  // explicit finnetApp exclusion as showFinnetTabNavigation. A plain method, not computed():
  // isMainPage is a plain @Input(), see showFinnetBreadcrumb() above.
  showFinnetSubpageWrapper(): boolean {
    return this.layout.isFinnetDesktop() && !this.isMainPage && !this.isInfoPage;
  }

  // The AI chat card (AI-664), not on a tab's info page; the card hides itself otherwise.
  // isInfoPage is a plain @Input(), see showFinnetBreadcrumb() above.
  showChatCard(): boolean {
    return !this.isInfoPage;
  }

  // Zero desktop subpages only — the Zero counterpart to showFinnetSubpageWrapper() above,
  // same isMainPage-is-a-plain-@Input() reasoning.
  showZeroSubpageWrapper(): boolean {
    return this.layout.isZeroDesktop() && !this.isMainPage && !this.isInfoPage;
  }
}
